/**
 * Formats investment profile field values for display.
 * Shared across member-app and grove-app.
 */

import { transformEnumToDisplay } from '@fruitful/fruitils';

import { getCountryName } from './countries';
import {
    InvestmentPortfolioLoss,
    NetWorth,
    ResidencyStatus,
    type UserInvestmentProfile,
} from './types';

export const formatInvestmentProfileValue = (
    field: keyof UserInvestmentProfile,
    value: string | undefined | null,
): string => {
    if (value == null) {
        return 'Not answered';
    }

    /* Citizenship: stored as ISO 3166-1 alpha-2 code (e.g. 'US', 'GB'). */
    if (field === 'citizenship') {
        return getCountryName(value);
    }

    /* Residency status */
    if (field === 'residency') {
        return formatResidencyStatus(value as ResidencyStatus);
    }

    /* Special formatting for NetWorth enum */
    if (field === 'netWorth') {
        return formatNetWorth(value as NetWorth);
    }

    /* Special formatting for InvestmentPortfolioLoss enum */
    if (field === 'investmentPortfolioLoss') {
        return formatInvestmentPortfolioLoss(value as InvestmentPortfolioLoss);
    }

    /* Default: use transformEnumToDisplay for all other enum values */
    return transformEnumToDisplay(value);
};

function formatResidencyStatus(status: ResidencyStatus): string {
    switch (status) {
        case ResidencyStatus.PERMANENT_RESIDENT:
            return 'Permanent Resident';
        case ResidencyStatus.TEMPORARY_RESIDENT:
            return 'Temporary Resident';
        case ResidencyStatus.NONRESIDENT:
            return 'Nonresident';
        case ResidencyStatus.NOT_APPLICABLE:
            return 'N/A';
        default:
            return transformEnumToDisplay(status);
    }
}

function formatNetWorth(netWorth: NetWorth): string {
    switch (netWorth) {
        case NetWorth.BETWEEN_0_50000:
            return '$0 - $50,000';
        case NetWorth.BETWEEN_50000_100000:
            return '$50,000 - $100,000';
        case NetWorth.BETWEEN_100000_250000:
            return '$100,000 - $250,000';
        case NetWorth.PLUS_250000:
            return '$250,000+';
        default:
            return transformEnumToDisplay(netWorth);
    }
}

function formatInvestmentPortfolioLoss(
    portfolioLoss: InvestmentPortfolioLoss,
): string {
    if (portfolioLoss === InvestmentPortfolioLoss.HOLD_NOTHING) {
        return 'Hold and do nothing';
    }
    return transformEnumToDisplay(portfolioLoss);
}
